import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { featureIsEnabled, useFeatures } from '../features';
import { bundlesForKey } from '../localizedText';
import { Messages, useBundles } from '../messages';
import { toArbValueId } from '../../utils/arbIds';
import { LOCALIZATION } from '../../utils/constants';

/**
 * The option groups of a list the legacy source shapes as a two-level grouped Select, so the ARB
 * list can carry the same headers instead of flattening them away.
 *
 * The groups own the value codes and their order; a list's flat `values` is derived by concatenating
 * them, so there is one source of truth for the codes.
 */
export type OptionGroups = {
  /** ARB id prefix the group headers are keyed under: `<idPrefix>_<id>`, e.g. `focus_group`. */
  idPrefix: string;
  /** The groups, in display order. */
  items: readonly { id: string; values: readonly string[] }[];
};

/** Where an option list's labels come from, in each of the two systems. */
export type LocalizedOptionList = {
  /** Key of the legacy i18next list, read wholesale when the Localization flag is off. */
  i18nKey: string;
  /** ARB id prefix the labels are keyed under: `<idPrefix>_<value>`, e.g. `focus_zone`. */
  idPrefix: string;
  /** The value codes, in display order. Code-owned — never derived from the bundle. */
  values: readonly string[];
  /** Also read `<idPrefix>_<value>_description`, for a list whose options carry descriptions. */
  withDescriptions?: boolean;
  /**
   * The option groups, for a grouped list. Omitted for a flat one. Present means the ARB list is
   * built grouped, matching the legacy shape, so a caller can render either straight into a Select.
   */
  groups?: OptionGroups;
};

/** Flag off: the legacy i18next list, whatever shape it holds (`focus` and `activity` are grouped). */
function useLegacyOptions(namespaces: string[], i18nKey: string): SelectProps.Options {
  const { t } = useTranslation(namespaces);
  return t(i18nKey, { returnObjects: true }) as SelectProps.Options;
}

/**
 * Flag on: rebuild the list from the ARB bundle, which is a flat id→string map and so cannot return
 * an array. Null when the bundle cannot serve every label, so the caller can show the legacy list.
 *
 * `useArb` is passed in rather than read here so that, with the flag off, this reads nothing at all —
 * a failed language pack must not report errors on a path where ARB is inert.
 */
function useArbOptions(
  namespaces: string[],
  list: LocalizedOptionList,
  useArb: boolean
): SelectProps.Options | null {
  const bundles = (useArb && bundlesForKey(list.i18nKey, namespaces)) || [];
  const readers = useBundles(bundles);
  // `bundles` is a fresh array every render, so the memo below keys off this string instead and
  // rebuilds the names from it. Depending on the array itself would bust the memo on every render.
  const bundlesKey = bundles.join('\u0000');

  // Memoised because a list is rebuilt id by id: an event card re-rendering for an unrelated reason
  // would otherwise re-read every label of every list it shows.
  return useMemo(() => {
    // A namespace can span several bundles, so try each in the order t() would, and take the first
    // that holds the id. No id is in two of them, so the order never decides which string is served.
    const candidates = (bundlesKey ? bundlesKey.split('\u0000') : [])
      .map((bundle) => readers[bundle])
      .filter((reader): reader is Messages => reader !== undefined);
    if (candidates.length === 0) return null;

    const read = (id: string): string => {
      for (const reader of candidates) {
        const text = reader.format(id);
        if (text) return text;
      }
      return '';
    };

    // Blank means the pack is still loading or failed to load, or the id is missing from the source
    // bundles altogether. Null abandons the whole list rather than filling the gap, so nobody is
    // shown a half-translated one. A locale that merely lacks a translation is not this case:
    // arb-tools serves English from the default pack, and the list is built as normal.
    const buildOption = (value: string): SelectProps.Option | null => {
      const id = `${list.idPrefix}_${toArbValueId(value)}`;
      const label = read(id);
      const description = list.withDescriptions ? read(`${id}_description`) : '';
      if (!label || (list.withDescriptions && !description)) return null;
      return list.withDescriptions ? { value, label, description } : { value, label };
    };

    // A grouped list keeps its headers, so the two flag states render the same shape. A header label
    // is read like any other, and a missing one abandons the list for the same reason.
    if (list.groups) {
      const groups: SelectProps.OptionGroup[] = [];
      for (const group of list.groups.items) {
        const label = read(`${list.groups.idPrefix}_${toArbValueId(group.id)}`);
        if (!label) return null;
        const options: SelectProps.Option[] = [];
        for (const value of group.values) {
          const option = buildOption(value);
          if (!option) return null;
          options.push(option);
        }
        groups.push({ label, options });
      }
      return groups;
    }

    const options: SelectProps.Option[] = [];
    for (const value of list.values) {
      const option = buildOption(value);
      if (!option) return null;
      options.push(option);
    }
    return options;
  }, [bundlesKey, readers, list]);
}

/**
 * An option list's labels, from RedVelvetTranslations or the legacy i18next list, chosen by the
 * Localization flag. The replacement for `t(key, { returnObjects: true })`, which ARB bundles cannot
 * answer.
 *
 * `namespaces` is the array passed to useLocalizedText alongside it, and resolves `i18nKey` the same
 * way: a `ns:` prefix names the namespace, otherwise the first entry.
 *
 * Both systems return the same shape: a list that declares `groups` keeps its option groups either
 * way, and one that doesn't is flat either way. So the result can be rendered straight into a Select,
 * and a label looked up by value needs a group-aware helper (getOptionFromValue, findOption).
 */
export function useTOptions(namespaces: string[], list: LocalizedOptionList): SelectProps.Options {
  const useArb = featureIsEnabled(LOCALIZATION, useFeatures(), useSearchParams()[0]);
  const legacy = useLegacyOptions(namespaces, list.i18nKey);
  const localized = useArbOptions(namespaces, list, useArb);

  return localized ?? legacy;
}
